@let requiredSettingsForProducts = $requiredSettingsForProducts();
@let requestProduct = $requestProduct();
@let expanded = $expanded();
@let isLoading = !!(isLoading$ | async);
@let productSettingsError = $productSettingsError();
@let productSettingsAllCompleted = $productSettingsAllCompleted();
@let isCheckingRequiredFields = $isCheckingRequiredFields();
@let mayCompleteForcibly =
  requestProduct.failedAttemptToClose &&
  !requestProduct.isClosedForcingly &&
  !requestProduct.completed;
@let isInsideSummary = $isInsideSummary();

@if (requestProduct) {
  <div
    class="x-card x-card_size_m x-white min-h-[36px] mb-2 h-full box-border relative"
    [tuiSkeleton]="isLoading && isInitialLoad"
    [ngClass]="{ 'p-[20px]': isInsideSummary }"
    [class.pb-10!]="isInsideSummary || expanded"
  >
    <!-- Иконка проверки обязательных полей стоит в углу абсолютно — pb-10 оставляет
         под неё место, иначе на мобиле её перекрывает кнопка на всю ширину -->
    @if (isInsideSummary || expanded) {
      <tui-icon
        icon="@tui.refresh-ccw"
        [class.animate-spin]="isCheckingRequiredFields"
        [class.opacity-50]="isCheckingRequiredFields"
        [class.cursor-not-allowed]="isCheckingRequiredFields"
        [class.cursor-pointer]="!isCheckingRequiredFields"
        class="size-6 text-base absolute right-3 bottom-3 transition-opacity z-10"
        (click)="!isCheckingRequiredFields && performRequiredFieldsChecking()"
      />
    }
    <header
      class="flex items-center justify-between mt-1"
      (click)="toggleExpand($event, requestProduct.id)"
      [ngClass]="{
        'cursor-pointer': !isInsideSummary,
      }"
    >
      <div class="flex items-center">
        <x-request-product-icon
          [requestProduct]="requestProduct"
          [requestDevices]="$requestDevices()"
          [withCounter]="false"
          [iconSize]="RequestProductIconSize.Lg"
        ></x-request-product-icon>
        <h4
          class="flex items-center"
          [ngClass]="{
            'x-font-body-1-bold': isInsideSummary,
            'x-font-body-2-bold': !isInsideSummary,
          }"
        >
          @if (!isInsideSummary) {
            <tui-icon
              [style.border-width.rem]="0.25"
              [icon]="expanded ? '@tui.chevron-up' : '@tui.chevron-down'"
            ></tui-icon>
          }
          <span class="mt-0.5">
            {{
              requestProduct.requestProductId
                | entityTranslate: 'request_products'
            }}
            @if (requestProduct.amount > 1) {
              <span
                tuiBadge
                class="mx-1 font-medium! mb-1"
                [size]="isInsideSummary ? 'm' : 's'"
                (click)="$event.stopPropagation()"
                >{{ requestProduct.amount }}</span
              >
            }
          </span>
        </h4>
      </div>
      @if (requestProduct.completed) {
        @if (requestProduct.isClosedForcingly) {
          <tui-icon
            icon="@tui.check"
            [tuiHint]="'request_form.goods.hint_closed_forcingly' | translate"
            class="red-bg rounded-full text-white size-6 text-base"
          ></tui-icon>
        } @else {
          <tui-icon
            [style.border-width.rem]="0.25"
            size="l"
            [icon]="isInsideSummary ? '@tui.check' : '@tui.check'"
            [tuiHint]="
              'request_form.goods.hint_completed_successfully' | translate
            "
            class="x-bg-green rounded-full text-white cursor-pointer size-6"
            [class.text-base]="!isInsideSummary"
          ></tui-icon>
        }
      } @else {
        @if (permissionService.canCompleteProducts$ | async) {
          <button
            appearance="outline"
            tuiButton
            type="button"
            [style.border-radius.rem]="100"
            [size]="isInsideSummary ? 'm' : 's'"
            class="x-white complete-button ms-1"
            [disabled]="isLoading"
            (click)="
              mayCompleteForcibly
                ? closeProductForcibly($event)
                : toggleCompleteProduct($event)
            "
            [tuiSkeleton]="isLoading"
          >
            <span
              [ngClass]="
                isInsideSummary ? 'x-font-body-4-bold' : 'x-font-body-5-bold'
              "
            >
              {{ 'request_form.goods.button_complete' | translate }}
            </span>
          </button>
        }
      }
    </header>
    @if (isInsideSummary) {
      <ng-container [ngTemplateOutlet]="requiredProductSettings" />
    } @else {
      <tui-expand [expanded]="expanded">
        <ng-container [ngTemplateOutlet]="requiredProductSettings" />
      </tui-expand>
    }
  </div>
}

<ng-template #requiredProductSettings>
  <div
    class="w-full"
    [ngClass]="{
      'mt-6': isInsideSummary,
    }"
  >
    <div>
      @if (requestProduct.isClosedForcingly) {
        <span
          tuiChip
          size="m"
          iconLeft="@tui.circle-alert"
          class="my-5 h-auto! min-h-(--tui-height-l)"
        >
          <div class="x-font-body-4">
            <p class="font-semibold">
              {{ 'request_form.goods.closed_forcingly_reason' | translate }}
            </p>
            <p class="x-bg-red font-extrabold whitespace-normal">
              «{{ requestProduct.forcedClosureReason }}»
            </p>
          </div>
        </span>
      }

      @if (mayCompleteForcibly) {
        <span
          tuiChip
          size="m"
          iconLeft="@tui.circle-alert"
          class="my-5 h-auto! min-h-(--tui-height-l)"
        >
          <div class="x-font-body-4 whitespace-normal">
            <p class="font-semibold">
              {{
                'request_form.goods.failed_attempt_to_close_reason' | translate
              }}
            </p>
            <p class="x-text-red font-extrabold">
              {{ 'request_form.goods.not_all_conditions_are_met' | translate }}
            </p>
          </div>
        </span>
      }

      @if (requiredSettingsForProducts) {
        @if (requiredSettingsForProducts[requestProduct.requestProductId.id]) {
          <ol class="font-semibold">
            @for (
              setting of requiredSettingsForProducts[
                requestProduct.requestProductId.id
              ] | keyvalue;
              track setting.key
            ) {
              @if (
                setting.key === 'devicesControl' &&
                isArray(setting.value) &&
                setting.value.length > 0
              ) {
                <li class="mb-3">
                  <span class="opacity-60">{{
                    'request_form.goods.devices' | translate
                  }}</span>
                  <ul>
                    @for (value of setting.value; track value) {
                      <li>
                        {{ value }}
                      </li>
                    }
                  </ul>
                </li>
              }
              @if (
                setting.key === 'devices' && setting.value && +setting.value > 0
              ) {
                <li class="mb-3">
                  <span class="opacity-60">{{
                    'request_form.goods.install_devices' | translate
                  }}</span>
                  <span class="ml-1 x-text-red font-semibold">{{
                    setting.value
                  }}</span>
                  <span class="ml-1 font-semibold">{{
                    'request_form.goods.pieces' | translate
                  }}</span>
                </li>
              }
              @if (setting.key === 'comment' && setting.value === true) {
                <li class="opacity-60 mb-3">
                  {{ 'request_form.goods.write_comment' | translate }}
                </li>
              }
              @if (setting.key === 'dealDetails' && setting.value === true) {
                <li class="opacity-60 mb-3">
                  {{ 'request_form.goods.fill_deal_details' | translate }}
                </li>
              }
              @if (
                setting.key === 'fields' &&
                isArray(setting.value) &&
                setting.value.length > 0
              ) {
                <li class="mb-3">
                  <span class="opacity-60">{{
                    'request_form.goods.fill_fields' | translate
                  }}</span>
                  <ul>
                    @for (value of setting.value; track value) {
                      <li>
                        {{ value }}
                      </li>
                    }
                  </ul>
                </li>
              }
              @if (
                setting.key === 'files' &&
                isArray(setting.value) &&
                setting.value.length > 0
              ) {
                <li class="mb-3">
                  <span class="opacity-60">{{
                    'request_form.goods.upload_files' | translate
                  }}</span>
                  <ul>
                    @for (value of setting.value; track value) {
                      <li>
                        {{ value }}
                      </li>
                    }
                  </ul>
                </li>
              }
              @if (
                setting.key === 'custom' &&
                isArray(setting.value) &&
                setting.value.length > 0
              ) {
                <li class="mb-3">
                  <span class="opacity-60">{{
                    'request_form.goods.additionally' | translate
                  }}</span>
                  <ul>
                    @for (value of setting.value; track value) {
                      <li>
                        {{ value }}
                      </li>
                    }
                  </ul>
                </li>
              }
            }
          </ol>
        }
        @if (productSettingsError && !requestProduct.completed) {
          <div
            [innerHTML]="productSettingsError"
            class="font-semibold x-text-red"
          ></div>
        }
        @if (productSettingsAllCompleted && !requestProduct.completed) {
          <p class="font-semibold x-text-green">
            {{ productSettingsAllCompleted }}
          </p>
        }
      }
    </div>
    <!--    удаление товара-->
    <!--    <tui-svg-->
    <!--      src="@tui.trash-2"-->
    <!--      tuiHint="Товар был выполнен!"-->
    <!--      class="block! mt-5 rounded-full bg-[color:var(&#45;&#45;tui-neutral-bg)] h-[44px]! w-[44px]!"-->
    <!--    ></tui-svg>-->
    @if (
      requestProduct.isClosedForcingly &&
      (permissionService.canCompleteProducts$ | async)
    ) {
      <button
        tuiButton
        [style.border-radius.rem]="100"
        type="button"
        size="m"
        class="mt-2 mb-2 w-full"
        [disabled]="isLoading"
        [tuiSkeleton]="isLoading"
        (click)="cancelForciblyCompletion($event)"
      >
        {{ 'request_form.goods.button_cancel_forcibly_completion' | translate }}
      </button>
    }

    @if (
      requestProduct.completed &&
      !requestProduct.isClosedForcingly &&
      (permissionService.canCompleteProducts$ | async)
    ) {
      <div class="h-[42px]" [ngClass]="{ 'mt-5': !isInsideSummary }">
        <button
          appearance="outline"
          tuiButton
          type="button"
          [style.border-radius.rem]="100"
          size="m"
          [tuiSkeleton]="isLoading"
          [ngClass]="{ 'bottom-[20px]': isInsideSummary }"
          class="x-white complete-button absolute!"
          [disabled]="isLoading"
          (click)="toggleCompleteProduct($event)"
        >
          {{ 'request_form.goods.button_cancel_completion' | translate }}
        </button>
      </div>
    }
  </div>
</ng-template>
